/**
 * The decision AFTER the debrief: offer, or reject.
 *
 * "Mark debrief done" used to be the end of the final-round flow — it recorded
 * that the conversation had happened and left the recruiter with nowhere to say
 * what came out of it. So the actual outcome of the most expensive round in the
 * process was taken somewhere else, or not recorded at all (Theresa, Sep 2026).
 *
 * Both outcomes now live here, behind one explicit choice, and each does what
 * that outcome actually requires:
 *
 *  - OFFER moves the Lever stage and optionally opens the profile, because the
 *    offer itself is assembled in Lever. Same reasoning as OfferMoveBody, which
 *    this deliberately mirrors rather than re-implements differently.
 *  - REJECT archives with an official Lever reason and sends the rejection —
 *    the same words, the same Email Library template and the same AGG pipeline
 *    the Archive modal uses, because there must be exactly one rejection path.
 *
 * Nothing is armed by default. A decision this consequential should not be one
 * mis-click away, and "debrief done" is not itself a decision.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@finn-auto/design-system/atoms/input';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { RadioGroup, RadioItem } from '@finn-auto/design-system/atoms/radio';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { previewRejectionEmail } from '../../services/taActionBoardApi';
import type { SchedulingCandidate, StageRef } from '../../types/taActionBoard';
import { COLORS } from './format';
import { EmailLibraryLink } from './EmailLibraryLink';
import { LanguageToggle } from './LanguageToggle';
import { ModalActions } from './ModalActions';

/** What the recruiter decided. `null` = they closed without deciding. */
export type DebriefOutcome =
  | {
      kind: 'offer';
      targetStageId: string;
      /** Open the Lever profile — the offer details go there. */
      openLever: boolean;
    }
  | {
      kind: 'reject';
      reason: string;
      /** The approved rejection, or null to archive without sending. */
      email: { subject: string; bodyHtml: string; language: 'de' | 'en' } | null;
    };

/** Lever's offer stage, by id then by name — the id is the reliable half. */
function findOfferStage(stages: StageRef[]): StageRef | null {
  return (
    stages.find((s) => s.id === 'offer') ||
    stages.find((s) => /\boffer\b/i.test(s.name)) ||
    null
  );
}

export function DebriefDecisionBody({
  candidate,
  stages,
  archiveReasons,
  gmailConnected,
  onConfirm,
  onCancel,
}: {
  candidate: SchedulingCandidate;
  /** Lever's stages in pipeline order — the offer stage is picked from these. */
  stages: StageRef[];
  /** ACTIVE Lever archive reasons; the text must stay byte-exact (see the engine). */
  archiveReasons: string[];
  gmailConnected: boolean;
  onConfirm: (outcome: DebriefOutcome) => void;
  onCancel: () => void;
}) {
  const firstName = candidate.name.trim().split(/\s+/)[0] || candidate.name;
  const offerStage = findOfferStage(stages);

  // Nothing pre-selected: see the header note.
  const [decision, setDecision] = useState<'offer' | 'reject' | null>(null);
  const [stageId, setStageId] = useState(offerStage?.id || '');
  const [openLever, setOpenLever] = useState(true);
  const [reason, setReason] = useState(archiveReasons[0] || '');
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [skipEmail, setSkipEmail] = useState(false);

  /**
   * The rejection draft, in the chosen language. Fetched only once REJECT is
   * chosen — a candidate being offered a job should not cost a model call for
   * the rejection they are not getting.
   */
  const draftQuery = useQuery({
    queryKey: ['debrief-rejection', candidate.opportunityId, language],
    queryFn: () => previewRejectionEmail(candidate.opportunityId, language),
    enabled: decision === 'reject',
    retry: false,
    staleTime: 60_000,
  });
  const draft = draftQuery.data;

  // Edits are overrides on the fetched draft (null = follow it), so switching
  // language re-drafts cleanly instead of needing a syncing effect.
  const [subjectEdit, setSubjectEdit] = useState<string | null>(null);
  const [bodyEdit, setBodyEdit] = useState<string | null>(null);
  const subject = subjectEdit ?? draft?.subject ?? '';
  const body = bodyEdit ?? draft?.text ?? '';
  const candidateEmail = draft?.candidateEmail || candidate.email;
  // No template in the Library ⇒ no email, by standing decision. Archiving still works.
  const noTemplate = Boolean(draft?.emailError);
  /**
   * There is no email to send — for EITHER reason: the Library holds no copy for
   * this variant, or the draft could not be built at all.
   *
   * Both have to unblock the archive. Treating only the first as "no email"
   * left a recruiter who had chosen REJECT with a failed draft, an empty body
   * and a disabled confirm button — stuck, with the decision already made.
   */
  const emailUnavailable =
    noTemplate || draftQuery.isError || (!draftQuery.isFetching && !draft);
  const canSend = gmailConnected && !!candidateEmail && !emailUnavailable;
  const willSend = decision === 'reject' && !skipEmail && canSend;

  const valid =
    decision === 'offer'
      ? Boolean(stageId)
      : decision === 'reject'
        ? Boolean(reason) &&
          (skipEmail || emailUnavailable || (canSend && !!subject.trim() && !!body.trim()))
        : false;

  const confirmLabel =
    decision === 'offer'
      ? openLever
        ? 'Move to offer & open Lever'
        : 'Move to offer'
      : decision === 'reject'
        ? willSend
          ? `Send rejection to ${candidateEmail} & archive`
          : 'Archive without an email'
        : 'Pick an outcome';

  return (
    <>
      <ModalHeader>
        <ModalTitle>Debrief done — what did you decide about {firstName}?</ModalTitle>
      </ModalHeader>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          The debrief is recorded either way. This is the outcome of it, and it changes{' '}
          {firstName}’s stage in Lever.
        </p>

        <RadioGroup
          label="Outcome"
          value={decision ?? ''}
          onValueChange={(v: string) => setDecision(v as 'offer' | 'reject')}
        >
          <RadioItem
            value="offer"
            disabled={!offerStage && stages.length === 0}
            label={
              offerStage
                ? `Move to “${offerStage.name}” — we are making an offer`
                : 'Move to the offer stage — we are making an offer'
            }
          />
          <RadioItem value="reject" label="Reject — archive in Lever, with a rejection email" />
        </RadioGroup>

        {decision === 'offer' && (
          <div className="space-y-3 rounded-lg border border-gray-200 p-3">
            {/* Same wording as the Move-to-offer flow, on purpose: the offer is
                assembled in Lever, so no email is drafted here. */}
            <p className="text-sm text-gray-600">
              {firstName} moves to “{stages.find((s) => s.id === stageId)?.name || 'Offer'}” in
              Lever. No email is drafted — the offer itself is put together in Lever.
            </p>
            {stages.length > 0 && (
              <Select value={stageId} onValueChange={setStageId}>
                <SelectTrigger label="Move to stage" className="w-full">
                  <SelectValue placeholder="Choose a stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.id === offerStage?.id ? ' (offer)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <RadioGroup
              value={openLever ? 'open' : 'stay'}
              onValueChange={(v: string) => setOpenLever(v === 'open')}
            >
              <RadioItem value="open" label="Open their Lever profile so I can enter the offer now" />
              <RadioItem value="stay" label="Just move the stage — I will do the offer later" />
            </RadioGroup>
          </div>
        )}

        {decision === 'reject' && (
          <div className="space-y-3 rounded-lg border border-gray-200 p-3">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger label="Lever archive reason" className="w-full">
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {archiveReasons.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {draftQuery.isFetching && (
              <p className="text-xs text-gray-500">Drafting the rejection…</p>
            )}
            {draftQuery.isError && (
              <p
                className="rounded px-2 py-1 text-xs"
                style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
              >
                The rejection draft could not be built. You can still archive without an email.
              </p>
            )}
            {noTemplate && (
              <p className="text-xs text-gray-500">
                {draft?.emailError} Archiving still works — it just sends nothing.
              </p>
            )}

            {draft && !noTemplate && (
              <>
                {/* Above the box, like every other draft in the board. */}
                <LanguageToggle
                  value={language}
                  onChange={(next) => {
                    setSubjectEdit(null);
                    setBodyEdit(null);
                    setLanguage(next);
                  }}
                  hasEdits={subjectEdit !== null || bodyEdit !== null}
                />
                <Input
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubjectEdit((e.target as HTMLInputElement).value)}
                />
                <Textarea
                  label="Rejection email"
                  rows={5}
                  textAreaClassName="min-h-[14rem]"
                  value={body}
                  onChange={(e) => setBodyEdit(e.target.value)}
                />
                <div className="mt-1 text-right">
                  <EmailLibraryLink templateKey={draft.templateKey} />
                </div>
                <RadioGroup
                  value={skipEmail ? 'no-email' : 'send'}
                  onValueChange={(v: string) => setSkipEmail(v === 'no-email')}
                >
                  <RadioItem
                    value="send"
                    disabled={!canSend}
                    label={
                      canSend
                        ? `Send this to ${candidateEmail} and archive`
                        : gmailConnected
                          ? 'No candidate email on the Lever profile — nothing can be sent'
                          : 'Connect Google on the board to send from here'
                    }
                  />
                  <RadioItem
                    value="no-email"
                    label="Archive WITHOUT an email — the hiring manager handles the feedback call"
                  />
                </RadioGroup>
                <p className="text-xs text-gray-500">
                  {skipEmail
                    ? 'Archived in Lever with the reason above. Nothing is sent.'
                    : 'Sends from your own Gmail after a 10-second countdown you can stop, then archives in Lever. It lands in your Sent folder, so a reply comes back to you.'}
                </p>
              </>
            )}
          </div>
        )}
      </div>
      <ModalActions
        confirmLabel={confirmLabel}
        confirmDisabled={!valid}
        onConfirm={() => {
          if (decision === 'offer') {
            // Opened from the click handler so the browser treats it as
            // user-initiated; a popup blocker would otherwise swallow it.
            if (openLever) window.open(candidate.leverUrl, '_blank', 'noopener,noreferrer');
            onConfirm({ kind: 'offer', targetStageId: stageId, openLever });
            return;
          }
          if (decision === 'reject') {
            onConfirm({
              kind: 'reject',
              reason,
              email: willSend ? { subject, bodyHtml: body, language } : null,
            });
          }
        }}
        onCancel={onCancel}
      />
    </>
  );
}
